import { OPERATIONS_FEATURE_FLAG } from "components/crane/featureFlags";
import BlenderOutlinedIcon from "@mui/icons-material/BlenderOutlined";
import { PROJECTIONS_FEATURE_FLAG } from "components/projections/featureFlags";
import { ANALYTICS_PERMISSIONS } from "components/strategyDashboard/permissions";
import {
  ADD_MISSING_OVERRIDES_PERMISSIONS,
  HEAT_QUEUE_PERMISSIONS,
  OVERRIDE_HISTORY_PERMISSIONS,
  OVERRIDE_RECIPES_PERMISSIONS,
  getOverridesPermissions,
  overrideHistoryEditingEnabled,
  OVERRIDES_TENANT_SETTINGS,
} from "components/heatQueue/permissions";
import {
  OVERRIDES_PATH,
  OVERRIDE_ADD_MISSING_PATH,
  OVERRIDE_HISTORY_PATH,
  OVERRIDE_RECIPES_PATH,
} from "components/heatQueue/override/routes";
import { useTenantDataOptional } from "contexts/tenant";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CameraIcon from "@mui/icons-material/Camera";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CommentIcon from "@mui/icons-material/Comment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EditIcon from "@mui/icons-material/Edit";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import EngineeringIcon from "@mui/icons-material/Engineering";
import ExploreIcon from "@mui/icons-material/Explore";
import FactoryIcon from "@mui/icons-material/Factory";
import FlagIcon from "@mui/icons-material/Flag";
import FolderIcon from "@mui/icons-material/Folder";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import HistoryIcon from "@mui/icons-material/History";
import InsightsIcon from "@mui/icons-material/Insights";
import InventoryIcon from "@mui/icons-material/Inventory";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PeopleIcon from "@mui/icons-material/People";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import PsychologyIcon from "@mui/icons-material/Psychology";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import RuleIcon from "@mui/icons-material/Rule";
import ScienceIcon from "@mui/icons-material/Science";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import ViewTimelineIcon from "@mui/icons-material/ViewTimeline";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SavingsIcon from "@mui/icons-material/Savings";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import SpaceDashboardIcon from "@mui/icons-material/SpaceDashboard";
import StorageIcon from "@mui/icons-material/Storage";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TuneIcon from "@mui/icons-material/Tune";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import TvIcon from "@mui/icons-material/Tv";
import VideocamIcon from "@mui/icons-material/Videocam";
import VerifiedIcon from "@mui/icons-material/Verified";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SettingsIcon from "@mui/icons-material/Settings";
import { MenuItem } from "./types";
import {
  OVERVIEW_PERMISSIONS,
  useCraneDriverViewPermissions,
} from "hooks/permissions";
import { Permission, Tenant } from "src/store/api/generatedApi";
import { useMemo } from "react";

const getTenantMenuItems = ({
  craneDriverPermissions,
  heatOverrideSettings,
}: {
  craneDriverPermissions: Permission[];
  heatOverrideSettings: Pick<
    Partial<Tenant>,
    "heat_override_mode" | "heat_override_options"
  >;
}): MenuItem[] => [
  {
    path: "",
    i18nKey: "overview",
    icon: <SpaceDashboardIcon />,
    permissionsToCheck: OVERVIEW_PERMISSIONS,
  },
  {
    path: "optimiser",
    i18nKey: "optimiser",
    icon: <SmartToyIcon />,
    featureFlag: "optimiser",
    permissionsToCheck: ["read"],
  },
  {
    path: "crane/supervisor/standard/latest",
    i18nKey: "operations",
    icon: <EngineeringIcon />,
    featureFlag: OPERATIONS_FEATURE_FLAG,
    permissionsToCheck: ["crane_supervisor", "crane_supervisor_viewer"],
  },
  {
    path: "deployed-plans",
    i18nKey: "deployedPlans",
    icon: <AssignmentTurnedInIcon />,
    featureFlag: "optimiser",
    requireDeploy: true,
    permissionsToCheck: ["read"],
  },
  {
    path: "crane/driver",
    i18nKey: "crane",
    icon: <PrecisionManufacturingIcon />,
    requireCranePages: true,
    permissionsToCheck: craneDriverPermissions,
  },
  {
    path: "heat-queue",
    i18nKey: "heatQueue",
    icon: <ViewTimelineIcon />,
    requireHeatOverrides: true,
    permissionsToCheck: HEAT_QUEUE_PERMISSIONS,
  },
  {
    path: OVERRIDES_PATH,
    i18nKey: "overrides",
    icon: <TuneIcon />,
    requireHeatOverrides: true,
    permissionsToCheck: getOverridesPermissions(heatOverrideSettings),
    children: [
      {
        path: `${OVERRIDES_PATH}/${OVERRIDE_HISTORY_PATH}`,
        i18nKey: "history",
        icon: <HistoryIcon />,
        permissionsToCheck: OVERRIDE_HISTORY_PERMISSIONS,
      },
      ...(heatOverrideSettings.heat_override_mode ===
      OVERRIDES_TENANT_SETTINGS.heat_override_mode
        ? ([
            {
              path: `${OVERRIDES_PATH}/${OVERRIDE_RECIPES_PATH}`,
              i18nKey: "recipes",
              icon: <TuneIcon />,
              permissionsToCheck: OVERRIDE_RECIPES_PERMISSIONS,
            },
          ] satisfies MenuItem[])
        : []),
      ...(overrideHistoryEditingEnabled(heatOverrideSettings)
        ? ([
            {
              path: `${OVERRIDES_PATH}/${OVERRIDE_ADD_MISSING_PATH}`,
              i18nKey: "addMissing",
              icon: <EditNoteIcon />,
              permissionsToCheck: ADD_MISSING_OVERRIDES_PERMISSIONS,
            },
          ] satisfies MenuItem[])
        : []),
    ],
  },
  {
    path: "dashboard",
    i18nKey: "analytics",
    icon: <AnalyticsIcon />,
    permissionsToCheck: ANALYTICS_PERMISSIONS,
    children: [
      {
        path: "dashboard/inventory",
        i18nKey: "inventory",
        icon: <InventoryIcon />,
        featureFlag: "strategy_dashboard_inventory",
        permissionsToCheck: ["strategy_dashboard_inventory", "analytics_admin"],
      },
      {
        path: "dashboard/consumption",
        i18nKey: "consumption",
        icon: <TrendingDownIcon />,
        featureFlag: "strategy_dashboard_consumption",
        permissionsToCheck: [
          "strategy_dashboard_consumption",
          "analytics_admin",
        ],
      },
      {
        path: "dashboard/production",
        i18nKey: "production",
        icon: <FactoryIcon />,
        featureFlag: "strategy_dashboard_production",
        permissionsToCheck: [
          "strategy_dashboard_production",
          "analytics_admin",
        ],
      },
      {
        path: "dashboard/quality",
        i18nKey: "quality",
        icon: <VerifiedIcon />,
        featureFlag: "strategy_dashboard_quality",
        permissionsToCheck: ["strategy_dashboard_quality", "analytics_admin"],
      },
      {
        path: "dashboard/cost",
        i18nKey: "cost",
        icon: <AttachMoneyIcon />,
        featureFlag: "strategy_dashboard_cost",
        permissionsToCheck: ["strategy_dashboard_cost", "analytics_admin"],
      },
      {
        path: "dashboard/prices",
        i18nKey: "prices",
        icon: <TrendingUpIcon />,
        featureFlag: "strategy_dashboard_prices",
        permissionsToCheck: ["strategy_dashboard_prices", "analytics_admin"],
      },
      {
        path: "dashboard/heats",
        i18nKey: "heats",
        icon: <WhatshotIcon />,
        featureFlag: "strategy_dashboard_heats",
        permissionsToCheck: ["strategy_dashboard_heats", "analytics_admin"],
      },
      {
        path: "dashboard/scrapChefUsage",
        i18nKey: "scrapChefUsage",
        icon: <InsightsIcon />,
        featureFlag: "strategy_dashboard_scrap_chef_usage",
        permissionsToCheck: [
          // Analytics administrators do not inherit access to this page.
          "strategy_dashboard_scrap_chef_usage",
        ],
      },
      {
        path: "dashboard/savings",
        i18nKey: "savings",
        icon: <SavingsIcon />,
        featureFlag: "strategy_dashboard_savings",
        permissionsToCheck: ["strategy_dashboard_savings", "analytics_admin"],
      },
      {
        path: "dashboard/deliveries",
        i18nKey: "deliveries",
        icon: <LocalShippingIcon />,
        featureFlag: "strategy_dashboard_deliveries",
        permissionsToCheck: [
          "strategy_dashboard_deliveries",
          "analytics_admin",
        ],
      },
      {
        path: "dashboard/compliance",
        i18nKey: "compliance",
        icon: <VerifiedIcon />,
        featureFlag: "strategy_dashboard_compliance",
        permissionsToCheck: [
          "strategy_dashboard_compliance",
          "analytics_admin",
        ],
      },
      {
        path: "dashboard/crewComparison",
        i18nKey: "crewComparison",
        icon: <PeopleIcon />,
        featureFlag: "strategy_dashboard_crew_comparison",
        permissionsToCheck: [
          "strategy_dashboard_crew_comparison",
          "analytics_admin",
        ],
      },
      {
        path: "dashboard/schedule",
        i18nKey: "schedule",
        icon: <CalendarMonthIcon />,
        featureFlag: "strategy_dashboard_schedule",
        permissionsToCheck: ["strategy_dashboard_schedule", "analytics_admin"],
      },
      {
        path: "dashboard/editing",
        i18nKey: "editing",
        icon: <EditIcon />,
        featureFlag: "strategy_dashboard_editing",
        permissionsToCheck: [
          // Analytics administrators do not inherit access to this page.
          "strategy_dashboard_editing",
        ],
      },
    ],
  },
  {
    path: "file-upload",
    i18nKey: "files",
    icon: <FolderIcon />,
    requiredPermissions: ["read", "upload_file_write"],
    children: [
      {
        path: "file-upload",
        i18nKey: "upload",
        icon: <CloudUploadIcon />,
      },
      {
        path: "file-explorer",
        i18nKey: "explorer",
        icon: <ExploreIcon />,
        requireAdmin: true,
      },
    ],
  },
  {
    path: "projections",
    i18nKey: "projections",
    icon: <QueryStatsIcon />,
    featureFlag: PROJECTIONS_FEATURE_FLAG,
    permissionsToCheck: ["manage_target_baskets"],
    children: [
      {
        path: "projections/consumption",
        i18nKey: "consumption",
        icon: <TrendingDownIcon />,
        permissionsToCheck: ["manage_target_baskets"],
      },
      {
        path: "projections/delivery-adjustments",
        i18nKey: "deliveryAdjustments",
        icon: <LocalShippingIcon />,
        permissionsToCheck: ["manage_target_baskets"],
      },
    ],
  },
  {
    path: "planning",
    i18nKey: "planning",
    icon: <PsychologyIcon />,
    featureFlag: "optimiser",
    permissionsToCheck: ["scenario_planning"],
    children: [
      {
        path: "planning/peak-shaving",
        i18nKey: "peakShaving",
        icon: <ElectricBoltIcon />,
        featureFlag: "peak_shaving_scenarios",
      },
    ],
  },
  {
    path: "inventory",
    i18nKey: "inventory",
    icon: <InventoryRoundedIcon />,
    featureFlag: "inventory_measurement",
    permissionsToCheck: ["read"],
  },
  {
    path: "live",
    i18nKey: "scrapEye",
    icon: <CameraIcon />,
    featureFlag: "scrap_eyes",
    permissionsToCheck: ["scrap_tracking_dashboard_viewer"],
    children: [
      {
        path: "tool/identify-events",
        i18nKey: "identifyEvents",
        icon: <ManageSearchIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_labelling"],
      },
      {
        path: "tool/view-labels",
        i18nKey: "viewLabels",
        icon: <AssignmentTurnedInIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_labelling"],
      },
      {
        path: "tool/viewer",
        i18nKey: "videoViewer",
        icon: <VideocamIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_labelling", "video_label_reviewing"],
      },
      {
        path: "tool/manual-review",
        i18nKey: "manualReview",
        icon: <HistoryIcon />,
        featureFlag: "scrap_eyes",
        requireAdmin: true,
      },
      {
        path: "admin/scrap-eye/model-dashboard",
        i18nKey: "modelDashboard",
        icon: <SmartToyIcon />,
        featureFlag: "scrap_eyes",
        requireAdmin: true,
      },
      {
        path: "tool/loading-window-editor",
        i18nKey: "loadingWindowEditor",
        icon: <ViewTimelineIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_label_reviewing"],
      },
      {
        path: "tool/heat-issues",
        i18nKey: "heatIssues",
        icon: <RuleIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_label_reviewing"],
      },
      {
        path: "tool/comment-review",
        i18nKey: "commentReview",
        icon: <CommentIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_label_reviewing"],
      },
      {
        path: "tool/targeted-review",
        i18nKey: "targetedReview",
        icon: <FlagIcon />,
        featureFlag: "targeted_review",
        permissionsToCheck: ["video_label_reviewing"],
      },
      {
        path: "manage-labellers",
        i18nKey: "manageLabellers",
        icon: <PeopleIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["manage_labellers"],
      },
      {
        path: "tool/location-exceptional-status",
        i18nKey: "exceptions",
        icon: <ReportProblemIcon />,
        featureFlag: "scrap_eyes",
        permissionsToCheck: ["video_label_reviewing"],
      },
      {
        path: "live/demo",
        requireAdmin: true,
        i18nKey: "demo",
        featureFlag: "scrap_eyes",
        icon: <TvIcon />,
      },
    ],
  },

  {
    path: "parameters",
    i18nKey: "defaultParameters",
    icon: <SettingsIcon />,
    featureFlag: "optimiser",
    permissionsToCheck: ["manage_target_baskets"],
    children: [
      {
        path: "parameters/baskets",
        i18nKey: "productionBaskets",
        icon: <ShoppingBasketIcon />,
        permissionsToCheck: ["manage_target_baskets"],
      },
      {
        path: "parameters/material-limits",
        i18nKey: "materialLimits",
        icon: <TuneIcon />,
        permissionsToCheck: ["manage_target_baskets"],
      },
      {
        path: "parameters/material-properties",
        i18nKey: "materialProperties",
        icon: <ScienceIcon />,
        permissionsToCheck: ["manage_target_baskets"],
      },
      {
        path: "parameters/search-parameters",
        i18nKey: "searchParameters",
        icon: <ManageSearchIcon />,
        permissionsToCheck: ["manage_target_baskets"],
      },
      {
        path: "parameters/fixed-mixes",
        i18nKey: "fixedMixOverrides",
        icon: <BlenderOutlinedIcon />,
        featureFlag: "fixed_mix_defaults",
        permissionsToCheck: ["manage_target_baskets"],
      },
    ],
  },
  {
    path: "admin",
    i18nKey: "admin",
    icon: <AdminPanelSettingsIcon />,
    requireAdmin: true,
    children: [
      {
        path: "admin/users",
        i18nKey: "manageUsers",
        icon: <PeopleIcon />,
        requireAdmin: true,
      },
      {
        path: "admin/permissions-overview",
        i18nKey: "permissionsOverview",
        icon: <RemoveRedEyeIcon />,
        requireAdmin: true,
      },
      {
        path: "admin/delivery-form",
        i18nKey: "deliveryForm",
        icon: <AssignmentIcon />,
        requireAdmin: true,
      },
      {
        path: "admin/cache-admin",
        i18nKey: "cache",
        icon: <StorageIcon />,
        requireAdmin: true,
      },
      {
        path: "admin/scrap-eye/data-dashboard",
        i18nKey: "scrapEyeData",
        icon: <DashboardIcon />,
        requireAdmin: true,
      },
      {
        path: "../scrap-eye/labelling-coverage",
        i18nKey: "labellingCoverage",
        icon: <VerifiedIcon />,
        requireAdmin: true,
      },
      {
        path: "../scrap-eye/labelling-cost",
        i18nKey: "labellingCostOverview",
        icon: <MonetizationOnIcon />,
        requireAdmin: true,
      },
    ],
  },
];

export const useTenantMenuItems = (): MenuItem[] => {
  const craneDriverPermissions = useCraneDriverViewPermissions();
  const {
    heat_override_mode: heatOverrideMode,
    heat_override_options: heatOverrideOptions,
  } = useTenantDataOptional();
  return useMemo(
    () =>
      getTenantMenuItems({
        craneDriverPermissions,
        heatOverrideSettings: {
          heat_override_mode: heatOverrideMode,
          heat_override_options: heatOverrideOptions,
        },
      }),
    [craneDriverPermissions, heatOverrideMode, heatOverrideOptions]
  );
};
